import type { Bounds } from './use-interactive'

export function boundsEqual(a: Bounds, b: Bounds): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
}

export function isPointInBounds(bounds: Bounds, x: number, y: number): boolean {
  return x >= bounds.x && x <= bounds.x + bounds.width && y >= bounds.y && y <= bounds.y + bounds.height
}

export function boundsRecordsEqual(a: Record<string, Bounds>, b: Record<string, Bounds>): boolean {
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  for (const key of aKeys) {
    const next = b[key]
    if (!next || !boundsEqual(a[key], next)) return false
  }
  return true
}

type Listener = () => void

/**
 * Process-wide registry of the DOM elements that each `<InteractiveTracker />`
 * marks as interactive. A single measurement loop (see `use-bounds-sync`) reads
 * every registered element's rect in one batch, so N trackers cost one timer,
 * one reflow, and one atom write per tick — not N of each. Registration itself
 * touches no React state, so mounting a tracker triggers zero re-renders.
 */
/**
 * How long a still-connected element keeps its last measured rect after it
 * starts reading zero-size. Re-layouts (e.g. a sidepanel view swap resizing
 * the panel band) can leave an element 0×0 for a few frames; dropping the
 * region instantly makes the window click-through under a stationary cursor,
 * which re-routes in-progress multi-touch gestures to the app behind.
 */
const ZERO_SIZE_GRACE_MS = 250

class InteractiveRegistry {
  private elements = new Map<string, HTMLElement>()
  private listeners = new Set<Listener>()
  private measurementListeners = new Set<Listener>()
  private lastMeasuredBounds: Record<string, Bounds> = {}
  private zeroSizeSince = new Map<string, number>()

  /**
   * The most recent `measure()` result. The cursor hit-test reads this instead
   * of React state so it never trails a busy renderer's commit queue — the
   * atom copy is only for React consumers (debugger).
   */
  get lastMeasured(): Record<string, Bounds> {
    return this.lastMeasuredBounds
  }

  register(id: string, element: HTMLElement): void {
    this.elements.set(id, element)
    this.notify()
  }

  unregister(id: string): void {
    this.zeroSizeSince.delete(id)
    if (this.elements.delete(id)) this.notify()
  }

  /** Notifies on every register/unregister so the loop can start/stop itself. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  subscribeToMeasurementRequests(listener: Listener): () => void {
    this.measurementListeners.add(listener)
    return () => {
      this.measurementListeners.delete(listener)
    }
  }

  requestMeasurement(): void {
    for (const listener of this.measurementListeners) listener()
  }

  get size(): number {
    return this.elements.size
  }

  /**
   * Read every registered element's bounds in one batch. Zero-size boxes are
   * dropped, except that a still-connected element briefly keeps its previous
   * rect (see ZERO_SIZE_GRACE_MS) so transient re-layout collapses don't
   * flicker the window click-through.
   */
  measure(): Record<string, Bounds> {
    const now = Date.now()
    const record: Record<string, Bounds> = {}
    for (const [id, element] of this.elements) {
      const r = element.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) {
        this.zeroSizeSince.delete(id)
        record[id] = { x: r.left, y: r.top, width: r.width, height: r.height }
        continue
      }
      const last = this.lastMeasuredBounds[id]
      if (!last || !element.isConnected) {
        this.zeroSizeSince.delete(id)
        continue
      }
      const since = this.zeroSizeSince.get(id) ?? now
      this.zeroSizeSince.set(id, since)
      if (now - since < ZERO_SIZE_GRACE_MS) {
        record[id] = last
      }
    }
    this.lastMeasuredBounds = record
    return record
  }

  private notify(): void {
    for (const listener of this.listeners) listener()
  }
}

export const interactiveRegistry = new InteractiveRegistry()
